import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Box,
  Avatar
} from '@mui/material';
import {
  Dashboard,
  People,
  PersonAdd,
  LocalHospital,
  CalendarToday,
  Message,
  Analytics,
  MonitorHeart,
  BusinessCenter,
  VideoLibrary,
  EmojiEmotions
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fixS3Url } from '../../utils/s3Utils';
import logo from '../../images/logo.jpeg';

const DRAWER_WIDTH = 240;

interface SidebarItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  roles: ('superadmin' | 'doctor' | 'marketinguser')[];
}

const sidebarItems: SidebarItem[] = [
  {
    text: 'Dashboard',
    icon: <Dashboard />,
    path: '/dashboard',
    roles: ['superadmin', 'doctor', 'marketinguser']
  },
  {
    text: 'Doctors',
    icon: <LocalHospital />,
    path: '/doctors',
    roles: ['superadmin', 'marketinguser']
  },
  {
    text: 'Market Executives',
    icon: <BusinessCenter />,
    path: '/market-executives',
    roles: ['superadmin']
  },
  {
    text: 'Patients',
    icon: <People />,
    path: '/patients',
    roles: ['superadmin', 'doctor', 'marketinguser']  // Added marketinguser here
  },
  {
    text: 'Appointments',
    icon: <CalendarToday />,
    path: '/appointments',
    roles: ['superadmin', 'doctor']
  },
  {
    text: 'Health Monitor',
    icon: <MonitorHeart />,
    path: '/health-monitor',
    roles: ['superadmin', 'doctor']
  },
  {
    text: 'CMS',
    icon: <VideoLibrary />,
    path: '/cms',
    roles: ['superadmin']
  },
  {
    text: 'Motivational Messages',
    icon: <EmojiEmotions />,
    path: '/motivational-messages',
    roles: ['superadmin']
  },
  // {
  //   text: 'Messages',
  //   icon: <Message />,
  //   path: '/messages',
  //   roles: ['superadmin', 'doctor']
  // },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const filteredItems = sidebarItems.filter(item =>
    user && item.roles.includes(user.role)
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
        },
      }}
    >
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Box
            component="img"
            src={logo}
            alt="Cardireach Logo"
            sx={{
              width: 60,
              height: 60,
              objectFit: 'contain'
            }}
          />

        </Box>
      </Toolbar>

      <Divider />

      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          src={fixS3Url(user?.profile_image) ? `${fixS3Url(user?.profile_image)}?t=${Date.now()}` : undefined}
          sx={{
            bgcolor: user?.profile_image ? 'transparent' : 'primary.main',
            border: user?.profile_image ? '2px solid #e0e0e0' : 'none'
          }}
        >
          {!user?.profile_image && ((user?.full_name || user?.username)?.charAt(0) || 'U')}
        </Avatar>
        <Box>
          <Typography variant="subtitle2">{user?.full_name || user?.username}</Typography>
          <Typography variant="caption" color="textSecondary">
            {user?.role === 'superadmin' ? 'Super Admin' : user?.role === 'doctor' ? 'Doctor' : 'Marketing User'}
          </Typography>
        </Box>
      </Box>

      <Divider />

      <List>
        {filteredItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
}